import { Title } from '@solidjs/meta'
import { type Component, onCleanup, onMount } from 'solid-js'
import Accordion from '~/components/Accordion'
import AccordionGroup from '~/components/AccordionGroup'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import { mailto, SITE, whatsapp } from '~/data/site'
import reveal from '~/directives/reveal'
import { getGsap, prefersReducedMotion } from '~/utils/gsap'

void reveal

import Container from '~/components/Container'

const LandingLayout: Component = () => {
  let heroRef: HTMLElement | undefined
  let panelRef: HTMLDivElement | undefined

  const fillCounters = () => {
    heroRef?.querySelectorAll<HTMLElement>('[data-countup]').forEach(el => {
      el.textContent = (el.dataset.prefix ?? '') + el.dataset.countup + (el.dataset.suffix ?? '')
    })
  }

  onMount(() => {
    const g = getGsap()
    if (!g || prefersReducedMotion() || !heroRef) {
      fillCounters()
      return
    }

    const ctx = g.context(() => {
      const tl = g.timeline({ defaults: { ease: 'power3.out' } })
      tl.from('[data-hero-badge]', { y: 18, opacity: 0, duration: 0.5 })
        .from('[data-hero-line]', { yPercent: 115, opacity: 0, duration: 0.9, stagger: 0.12, ease: 'power4.out' }, '-=0.2')
        .from('[data-hero-sub]', { y: 20, opacity: 0, duration: 0.6 }, '-=0.5')
        .from('[data-hero-cta]', { y: 16, opacity: 0, duration: 0.5 }, '-=0.4')
        .from('[data-hero-stat]', { y: 16, opacity: 0, duration: 0.5, stagger: 0.08 }, '-=0.3')

      if (panelRef) {
        tl.from(panelRef, { y: 70, opacity: 0, scale: 0.94, duration: 1.0, ease: 'power4.out' }, '-=1.1')
        // Flotación continua sutil del panel
        g.to(panelRef, { y: '+=14', duration: 3.2, ease: 'sine.inOut', repeat: -1, yoyo: true })
      }

      // Count-up de las métricas
      heroRef?.querySelectorAll<HTMLElement>('[data-countup]').forEach(el => {
        const end = Number(el.dataset.countup) || 0
        const prefix = el.dataset.prefix ?? ''
        const suffix = el.dataset.suffix ?? ''
        const obj = { v: 0 }
        tl.to(
          obj,
          {
            v: end,
            duration: 1.2,
            ease: 'power2.out',
            onUpdate: () => {
              el.textContent = prefix + Math.round(obj.v) + suffix
            }
          },
          '-=0.9'
        )
      })
    }, heroRef)

    onCleanup(() => ctx.revert())
  })

  return (
    <>
      <Title>Florez Jaramillo Solutions - Desarrollo de Software & Soluciones Digitales</Title>
      <div class="flex flex-col gap-24">
        {/* HERO PREMIUM — adapta claro/oscuro vía tokens */}
        <section ref={heroRef} class="relative w-full overflow-hidden bg-background text-foreground pt-28 pb-24 md:pt-32 md:pb-28">
          {/* Mesh de gradientes + grid de puntos (translúcidos: válidos en claro y oscuro) */}
          <div class="pointer-events-none absolute inset-0 overflow-hidden">
            <div class="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(circle,rgba(209,58,68,0.18),transparent_60%)] blur-3xl"></div>
            <div class="absolute top-1/4 -left-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(7,141,238,0.14),transparent_60%)] blur-3xl"></div>
            <div class="absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.12),transparent_60%)] blur-3xl"></div>
            <div class="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(rgba(128,137,158,0.5)_1px,transparent_1.5px)] [background-size:22px_22px]"></div>
          </div>

          <Container class="relative z-10 flex flex-col items-center text-center">
            {/* Badge */}
            <div data-hero-badge class="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8 text-sm bg-surface/70 border border-border backdrop-blur-sm">
              <span class="relative flex h-2 w-2">
                <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
              </span>
              <span class="text-foreground-muted">Agencia de software · Disponibles para nuevos proyectos</span>
            </div>

            {/* Titular con clip-reveal por línea */}
            <h1 class="text-4xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8 text-foreground">
              <span class="block overflow-hidden pb-1">
                <span data-hero-line class="block">
                  Convertimos tu visión
                </span>
              </span>
              <span class="block overflow-hidden pb-2">
                <span data-hero-line class="block bg-gradient-to-r from-primary-500 via-rose-500 to-primary-600 text-transparent bg-clip-text">
                  en software exitoso
                </span>
              </span>
            </h1>

            <p data-hero-sub class="max-w-2xl mx-auto text-lg md:text-xl text-foreground-muted leading-relaxed mb-10">
              Desarrollamos aplicaciones web y móviles que <strong class="text-foreground">impulsan tu negocio</strong>. De MVP a producto escalable, con resultados medibles en{' '}
              <span class="text-primary font-semibold">30–90 días</span>.
            </p>

            <div data-hero-cta class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Button
                startIcon="material-symbols-light:rocket-launch-outline"
                color="primary"
                size="lg"
                class="px-8 py-4 text-lg font-semibold shadow-2xl shadow-primary-500/30 hover:scale-105 transition-transform duration-300"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Evaluar mi proyecto gratis
              </Button>
              <button
                type="button"
                class="inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold rounded-lg border border-border text-foreground hover:bg-surface-hover transition-colors duration-300 cursor-pointer"
                onClick={() => document.getElementById('casos-exito')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Icon icon="material-symbols-light:play-circle-outline" size={22} />
                Ver casos de éxito
              </button>
            </div>

            {/* Métricas con count-up */}
            <div class="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 w-full max-w-3xl mb-16">
              <div data-hero-stat class="text-center">
                <div class="text-3xl md:text-4xl font-black text-foreground" data-countup="50" data-prefix="+">
                  +50
                </div>
                <div class="text-sm text-foreground-subtle mt-1">Proyectos entregados</div>
              </div>
              <div data-hero-stat class="text-center">
                <div class="text-3xl md:text-4xl font-black text-foreground" data-countup="98" data-suffix="%">
                  98%
                </div>
                <div class="text-sm text-foreground-subtle mt-1">Satisfacción</div>
              </div>
              <div data-hero-stat class="text-center">
                <div class="text-3xl md:text-4xl font-black text-foreground" data-countup="30">
                  30
                </div>
                <div class="text-sm text-foreground-subtle mt-1">Días promedio MVP</div>
              </div>
              <div data-hero-stat class="text-center">
                <div class="text-3xl md:text-4xl font-black text-foreground">24/7</div>
                <div class="text-sm text-foreground-subtle mt-1">Soporte</div>
              </div>
            </div>

            {/* Panel / dashboard mockup flotante */}
            <div ref={panelRef} class="relative w-full max-w-4xl">
              <div class="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-primary-500/40 via-blue-500/30 to-violet-500/40 blur-md opacity-60"></div>
              <div class="relative rounded-2xl border border-border bg-surface/90 backdrop-blur-xl shadow-2xl overflow-hidden">
                {/* Barra superior */}
                <div class="flex items-center gap-2 px-4 py-3 border-b border-border">
                  <span class="h-3 w-3 rounded-full bg-red-400/80"></span>
                  <span class="h-3 w-3 rounded-full bg-amber-400/80"></span>
                  <span class="h-3 w-3 rounded-full bg-emerald-400/80"></span>
                  <div class="ml-3 h-5 flex-1 max-w-xs rounded-md bg-surface-muted"></div>
                </div>
                {/* Cuerpo del dashboard */}
                <div class="grid grid-cols-3 gap-4 p-5 text-left">
                  <div class="col-span-1 space-y-3">
                    <div class="rounded-xl bg-surface-muted border border-border p-4">
                      <div class="text-xs text-foreground-subtle">Ingresos</div>
                      <div class="text-xl font-bold text-foreground mt-1">$48.2k</div>
                      <div class="inline-flex items-center gap-1 text-xs text-emerald-500 mt-1">
                        <Icon icon="material-symbols-light:expand-less" width={12} /> 12.4%
                      </div>
                    </div>
                    <div class="rounded-xl bg-surface-muted border border-border p-4">
                      <div class="text-xs text-foreground-subtle">Usuarios</div>
                      <div class="text-xl font-bold text-foreground mt-1">15.3k</div>
                      <div class="inline-flex items-center gap-1 text-xs text-emerald-500 mt-1">
                        <Icon icon="material-symbols-light:expand-less" width={12} /> 8.1%
                      </div>
                    </div>
                  </div>
                  <div class="col-span-2 rounded-xl bg-surface-muted border border-border p-4 flex flex-col">
                    <div class="text-xs text-foreground-subtle mb-3">Rendimiento</div>
                    <div class="flex-1 flex items-end gap-2">
                      <div class="flex-1 rounded-t bg-gradient-to-t from-primary-500/40 to-primary-400 h-[40%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-primary-500/40 to-primary-400 h-[65%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-blue-500/40 to-blue-400 h-[50%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-blue-500/40 to-blue-400 h-[80%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-violet-500/40 to-violet-400 h-[60%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-violet-500/40 to-violet-400 h-[95%]"></div>
                      <div class="flex-1 rounded-t bg-gradient-to-t from-emerald-500/40 to-emerald-400 h-[72%]"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* POR QUÉ ELEGIRNOS - Nueva sección */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="secondary" class="mb-6">
                ¿Por qué somos diferentes?
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">No somos una agencia más</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">Combinamos experiencia técnica, metodologías probadas y un enfoque obsesivo en resultados de negocio.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Diferenciador 1 */}
              <Card class="p-8 text-center hover:shadow-xl transition-all duration-300 group border-t-4 border-t-primary-500">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-500/15 to-primary-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:trending-up" size={32} class="text-primary" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">Velocidad sin sacrificar calidad</h3>
                <p class="text-foreground-subtle leading-relaxed mb-6">Metodologías ágiles + stack tecnológico moderno = MVPs en 30 días con código production-ready desde el día uno.</p>
                <div class="flex items-center justify-center gap-2 text-sm font-medium text-primary">
                  <Icon icon="material-symbols-light:check-circle-outline" size={16} />
                  Sprint de 1-2 semanas
                </div>
              </Card>

              {/* Diferenciador 2 */}
              <Card class="p-8 text-center hover:shadow-xl transition-all duration-300 group border-t-4 border-t-emerald-500">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:target" size={32} class="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">Enfoque en resultados medibles</h3>
                <p class="text-foreground-subtle leading-relaxed mb-6">
                  No solo desarrollamos software, creamos herramientas que impactan KPIs de negocio: conversión, retención, eficiencia operativa.
                </p>
                <div class="flex items-center justify-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  <Icon icon="material-symbols-light:check-circle-outline" size={16} />
                  Analytics desde día 1
                </div>
              </Card>

              {/* Diferenciador 3 */}
              <Card class="p-8 text-center hover:shadow-xl transition-all duration-300 group border-t-4 border-t-blue-500">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500/15 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:group-outline" size={32} class="text-info" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">Partnership real, no solo desarrollo</h3>
                <p class="text-foreground-subtle leading-relaxed mb-6">Te acompañamos desde la conceptualización hasta el crecimiento. Consultoría estratégica, lanzamiento y optimización continua.</p>
                <div class="flex items-center justify-center gap-2 text-sm font-medium text-info">
                  <Icon icon="material-symbols-light:check-circle-outline" size={16} />
                  Soporte post-lanzamiento
                </div>
              </Card>
            </div>
          </Container>
        </section>

        {/* CASOS DE ÉXITO - Nueva sección */}
        <section id="casos-exito" use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <div class="absolute inset-0 bg-surface-muted -z-10"></div>
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="success" class="mb-6">
                Casos de éxito reales
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">Proyectos que transforman negocios</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">
                Resultados concretos que hablan por sí solos. Estos son algunos de los proyectos que hemos llevado al éxito.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Caso 1 */}
              <Card class="p-6 hover:shadow-xl transition-all duration-300 group">
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-3">
                    <Chip variant="soft" color="primary" size="sm">
                      SaaS
                    </Chip>
                    <Icon icon="material-symbols-light:arrow-outward" size={20} class="text-slate-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                  <h3 class="text-xl font-bold text-foreground mb-2">Plataforma de Gestión Empresarial</h3>
                  <p class="text-foreground-subtle mb-4">Sistema web completo para automatizar procesos internos de una empresa de 200+ empleados.</p>
                </div>
                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-emerald-600">40%</div>
                    <div class="text-xs text-foreground-subtle">Reducción tiempo</div>
                  </div>
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-emerald-600">$50K</div>
                    <div class="text-xs text-foreground-subtle">Ahorro anual</div>
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" size="sm">
                    React
                  </Chip>
                  <Chip variant="soft" size="sm">
                    Node.js
                  </Chip>
                  <Chip variant="soft" size="sm">
                    PostgreSQL
                  </Chip>
                </div>
              </Card>

              {/* Caso 2 */}
              <Card class="p-6 hover:shadow-xl transition-all duration-300 group">
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-3">
                    <Chip variant="soft" color="warning" size="sm">
                      E-commerce
                    </Chip>
                    <Icon icon="material-symbols-light:arrow-outward" size={20} class="text-slate-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                  <h3 class="text-xl font-bold text-foreground mb-2">App de Delivery Premium</h3>
                  <p class="text-foreground-subtle mb-4">Aplicación móvil y web para delivery de comida gourmet con sistema de pedidos en tiempo real.</p>
                </div>
                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-blue-600">300%</div>
                    <div class="text-xs text-foreground-subtle">Aumento ventas</div>
                  </div>
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-blue-600">15K</div>
                    <div class="text-xs text-foreground-subtle">Usuarios activos</div>
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" size="sm">
                    React Native
                  </Chip>
                  <Chip variant="soft" size="sm">
                    Firebase
                  </Chip>
                  <Chip variant="soft" size="sm">
                    Stripe
                  </Chip>
                </div>
              </Card>

              {/* Caso 3 */}
              <Card class="p-6 hover:shadow-xl transition-all duration-300 group md:col-span-2 lg:col-span-1">
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-3">
                    <Chip variant="soft" color="secondary" size="sm">
                      HealthTech
                    </Chip>
                    <Icon icon="material-symbols-light:arrow-outward" size={20} class="text-slate-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                  <h3 class="text-xl font-bold text-foreground mb-2">Sistema de Telemedicina</h3>
                  <p class="text-foreground-subtle mb-4">Plataforma segura para consultas médicas virtuales con expedientes digitales y pagos integrados.</p>
                </div>
                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-purple-600">5K</div>
                    <div class="text-xs text-foreground-subtle">Consultas/mes</div>
                  </div>
                  <div class="text-center p-3 bg-surface-muted rounded-lg">
                    <div class="text-2xl font-bold text-purple-600">99.9%</div>
                    <div class="text-xs text-foreground-subtle">Uptime</div>
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" size="sm">
                    SolidJS
                  </Chip>
                  <Chip variant="soft" size="sm">
                    WebRTC
                  </Chip>
                  <Chip variant="soft" size="sm">
                    AWS
                  </Chip>
                </div>
              </Card>
            </div>
          </Container>
        </section>

        {/* SERVICIOS REDISEÑADOS */}
        <section id="servicios" use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="primary" class="mb-6">
                Nuestros servicios
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">Todo lo que necesitas para triunfar</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">
                Desde la idea inicial hasta el producto escalable. Un enfoque integral que cubre cada aspecto de tu transformación digital.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Desarrollo Web */}
              <Card class="p-8 hover:shadow-xl transition-all duration-300 group">
                <div class="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-blue-500/15 to-cyan-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:grid-view-outline" size={32} class="text-info" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">Desarrollo Web</h3>
                <p class="text-foreground-subtle mb-6 leading-relaxed">Aplicaciones web modernas, responsivas y de alto rendimiento que convierten visitantes en clientes.</p>
                <ul class="space-y-2 mb-6">
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">React, Next.js, SolidJS</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">PWAs & SPA optimizadas</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">SEO & Performance</span>
                  </li>
                </ul>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" color="primary" size="sm">
                    React
                  </Chip>
                  <Chip variant="soft" color="primary" size="sm">
                    TypeScript
                  </Chip>
                  <Chip variant="soft" color="primary" size="sm">
                    Next.js
                  </Chip>
                </div>
              </Card>

              {/* Cloud & DevOps */}
              <Card class="p-8 hover:shadow-xl transition-all duration-300 group">
                <div class="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-teal-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:cloud-outline" size={32} class="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">Cloud & DevOps</h3>
                <p class="text-foreground-subtle mb-6 leading-relaxed">Infraestructura cloud robusta y procesos automatizados que garantizan escalabilidad y alta disponibilidad.</p>
                <ul class="space-y-2 mb-6">
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">AWS, Google Cloud, Azure</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Docker & Kubernetes</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">CI/CD automatizado</span>
                  </li>
                </ul>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" color="success" size="sm">
                    AWS
                  </Chip>
                  <Chip variant="soft" color="success" size="sm">
                    Docker
                  </Chip>
                  <Chip variant="soft" color="success" size="sm">
                    GitHub Actions
                  </Chip>
                </div>
              </Card>

              {/* UX/UI & Consultoría */}
              <Card class="p-8 hover:shadow-xl transition-all duration-300 group">
                <div class="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-purple-500/15 to-pink-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:palette-outline" size={32} class="text-purple-600 dark:text-purple-400" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">UX/UI & Consultoría</h3>
                <p class="text-foreground-subtle mb-6 leading-relaxed">Diseño centrado en el usuario y consultoría estratégica para maximizar el impacto de tu producto digital.</p>
                <ul class="space-y-2 mb-6">
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Research & User Testing</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Design Systems</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Estrategia de producto</span>
                  </li>
                </ul>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" color="secondary" size="sm">
                    Figma
                  </Chip>
                  <Chip variant="soft" color="secondary" size="sm">
                    Design Thinking
                  </Chip>
                  <Chip variant="soft" color="secondary" size="sm">
                    Analytics
                  </Chip>
                </div>
              </Card>

              {/* IA & Automatización */}
              <Card class="p-8 hover:shadow-xl transition-all duration-300 group">
                <div class="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-orange-500/15 to-red-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:bolt-outline" size={32} class="text-orange-600 dark:text-orange-400" />
                </div>
                <h3 class="text-xl font-bold text-foreground mb-4">IA & Automatización</h3>
                <p class="text-foreground-subtle mb-6 leading-relaxed">Inteligencia artificial y automatización inteligente que optimiza procesos y potencia tu negocio.</p>
                <ul class="space-y-2 mb-6">
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Chatbots & Asistentes IA</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Automatización de procesos</span>
                  </li>
                  <li class="flex items-start gap-3 text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={16} class="text-emerald-500 mt-0.5" />
                    <span class="text-foreground-muted">Machine Learning & Analytics</span>
                  </li>
                </ul>
                <div class="flex flex-wrap gap-2">
                  <Chip variant="soft" color="warning" size="sm">
                    OpenAI
                  </Chip>
                  <Chip variant="soft" color="warning" size="sm">
                    Python
                  </Chip>
                  <Chip variant="soft" color="warning" size="sm">
                    TensorFlow
                  </Chip>
                </div>
              </Card>
            </div>

            {/* CTA de servicios */}
            <div class="mt-16 text-center">
              <div class="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary-soft to-primary-soft rounded-full border border-primary/50 mb-8">
                <Icon icon="material-symbols-light:star-outline" size={20} class="text-primary" />
                <span class="text-on-primary-soft font-semibold">Soluciones integradas para máximo impacto</span>
                <Icon icon="material-symbols-light:star-outline" size={20} class="text-primary" />
              </div>
              <p class="text-lg text-foreground-subtle max-w-3xl mx-auto leading-relaxed mb-8">
                No vendemos servicios por separado. Creamos ecosistemas completos que impulsan el crecimiento sostenible de tu negocio.
              </p>
              <Button
                size="lg"
                color="primary"
                startIcon="material-symbols-light:chat-outline"
                class="shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Discutir mi proyecto
              </Button>
            </div>
          </Container>
        </section>

        {/* DESARROLLO MÓVIL - SECCIÓN ESPECIALIZADA */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20 bg-gradient-to-br from-background via-surface to-background overflow-hidden">
          {/* Fondo decorativo */}
          <div class="absolute inset-0 overflow-hidden">
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-primary-500/10 via-secondary-500/5 to-primary-500/10 blur-3xl"></div>
            <div class="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-500/5 to-cyan-500/5 blur-2xl"></div>
            <div class="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-gradient-to-tr from-green-500/5 to-emerald-500/5 blur-2xl"></div>
          </div>

          <Container class="relative z-10">
            <div class="text-center mb-16">
              <Chip variant="soft" color="primary" class="mb-6">
                Desarrollo móvil
              </Chip>
              <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span class="text-foreground">iOS y Android de </span>
                <span class="bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 text-transparent bg-clip-text">nivel superior</span>
              </h2>
              <p class="max-w-4xl mx-auto text-lg text-foreground-muted leading-relaxed">Experiencias móviles enfocadas en producto: rendimiento, accesibilidad y conversión.</p>
            </div>{' '}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-16">
              {/* Desarrollo Cross-Platform */}
              <div class="flex flex-col items-center text-center">
                <div class="relative mb-6">
                  {/* Mockup de teléfono - más pequeño en tablet */}
                  <div class="mx-auto w-40 md:w-44 lg:w-48 h-80 md:h-88 lg:h-96 bg-gradient-to-b from-slate-300 to-slate-400 dark:from-slate-800 dark:to-slate-900 rounded-[2.25rem] lg:rounded-[2.5rem] p-2 shadow-xl">
                    <div class="w-full h-full bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 rounded-[2rem] p-3 lg:p-4 relative overflow-hidden">
                      {/* Notch */}
                      <div class="absolute top-2 left-1/2 -translate-x-1/2 w-14 lg:w-16 h-5 lg:h-6 bg-slate-800 dark:bg-slate-900 rounded-full"></div>
                      {/* Contenido del mockup */}
                      <div class="mt-6 lg:mt-8 space-y-2 lg:space-y-3">
                        <div class="h-2.5 lg:h-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full w-3/4"></div>
                        <div class="h-2.5 lg:h-3 bg-slate-400 dark:bg-slate-600 rounded-full w-1/2"></div>
                        <div class="h-12 lg:h-16 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-lg border border-blue-400/50"></div>
                        <div class="grid grid-cols-2 gap-1.5 lg:gap-2">
                          <div class="h-6 lg:h-8 bg-emerald-500/30 rounded border border-emerald-400/50"></div>
                          <div class="h-6 lg:h-8 bg-purple-500/30 rounded border border-purple-400/50"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="absolute -bottom-1 -right-1 lg:-bottom-2 lg:-right-2 w-10 lg:w-12 h-10 lg:h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <Icon icon="material-symbols-light:code" size={20} class="text-white" />
                  </div>
                </div>
                <h3 class="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-primary">Desarrollo Cross-Platform</h3>
                <p class="text-foreground-subtle mb-4 lg:mb-6 leading-relaxed text-sm lg:text-base">
                  Apps híbridas de alto rendimiento con <strong class="text-foreground">React Native, Flutter</strong> y<strong class="text-foreground"> Kotlin</strong>. Una sola base de código para
                  múltiples plataformas.
                </p>
                <div class="space-y-1.5 lg:space-y-2 w-full">
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-emerald-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Desarrollo rápido</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-emerald-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Código compartido</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-emerald-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Performance nativo</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-emerald-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Mantenimiento fácil</span>
                  </div>
                </div>
              </div>

              {/* iOS Development */}
              <div class="flex flex-col items-center text-center">
                <div class="relative mb-6">
                  {/* Mockup de iPhone */}
                  <div class="mx-auto w-40 md:w-44 lg:w-48 h-80 md:h-88 lg:h-96 bg-gradient-to-b from-slate-300 to-slate-400 dark:from-slate-800 dark:to-slate-900 rounded-[2.25rem] lg:rounded-[2.5rem] p-2 shadow-xl">
                    <div class="w-full h-full bg-gradient-to-b from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-slate-800 rounded-[2rem] p-3 lg:p-4 relative overflow-hidden">
                      {/* Notch */}
                      <div class="absolute top-2 left-1/2 -translate-x-1/2 w-14 lg:w-16 h-5 lg:h-6 bg-slate-800 dark:bg-slate-900 rounded-full"></div>
                      {/* Contenido iOS-like */}
                      <div class="mt-6 lg:mt-8 space-y-2 lg:space-y-3">
                        <div class="h-2.5 lg:h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full w-2/3"></div>
                        <div class="h-2.5 lg:h-3 bg-slate-400 dark:bg-slate-600 rounded-full w-1/3"></div>
                        <div class="h-12 lg:h-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-xl border border-blue-400/50"></div>
                        <div class="grid grid-cols-2 gap-1.5 lg:gap-2">
                          <div class="h-6 lg:h-8 bg-blue-500/30 rounded-lg border border-blue-400/50"></div>
                          <div class="h-6 lg:h-8 bg-indigo-500/30 rounded-lg border border-indigo-400/50"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="absolute -bottom-1 -right-1 lg:-bottom-2 lg:-right-2 w-10 lg:w-12 h-10 lg:h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <Icon icon="mdi:apple" size={20} class="text-white" />
                  </div>
                </div>
                <h3 class="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-info">iOS Development</h3>
                <p class="text-foreground-subtle mb-4 lg:mb-6 leading-relaxed text-sm lg:text-base">
                  <strong class="text-foreground">Swift, SwiftUI</strong> y patrones modernos para apps fluidas con acabado premium. Integración con el ecosistema Apple.
                </p>
                <div class="space-y-1.5 lg:space-y-2 w-full">
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-blue-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Performance crítico</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-blue-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Publicación y ASO</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-blue-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Accesibilidad</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-blue-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Mantenibilidad</span>
                  </div>
                </div>
              </div>

              {/* Android Development */}
              <div class="flex flex-col items-center text-center">
                <div class="relative mb-6">
                  {/* Mockup de Android */}
                  <div class="mx-auto w-40 md:w-44 lg:w-48 h-80 md:h-88 lg:h-96 bg-gradient-to-b from-slate-300 to-slate-400 dark:from-slate-800 dark:to-slate-900 rounded-[2.25rem] lg:rounded-[2.5rem] p-2 shadow-xl">
                    <div class="w-full h-full bg-gradient-to-b from-green-50 to-green-100 dark:from-green-900/30 dark:to-slate-800 rounded-[2rem] p-3 lg:p-4 relative overflow-hidden">
                      {/* Status bar */}
                      <div class="flex justify-between items-center mb-4 lg:mb-6">
                        <div class="flex gap-1">
                          <div class="w-1 h-1 bg-green-500 rounded-full"></div>
                          <div class="w-1 h-1 bg-green-500 rounded-full"></div>
                          <div class="w-1 h-1 bg-slate-400 dark:bg-slate-600 rounded-full"></div>
                        </div>
                        <div class="text-xs text-green-500">100%</div>
                      </div>
                      {/* Contenido Android-like */}
                      <div class="space-y-2 lg:space-y-3">
                        <div class="h-2.5 lg:h-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full w-3/4"></div>
                        <div class="h-2.5 lg:h-3 bg-slate-400 dark:bg-slate-600 rounded-full w-1/2"></div>
                        <div class="h-12 lg:h-16 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-lg border border-green-400/50"></div>
                        <div class="grid grid-cols-2 gap-1.5 lg:gap-2">
                          <div class="h-6 lg:h-8 bg-green-500/30 rounded border border-green-400/50"></div>
                          <div class="h-6 lg:h-8 bg-teal-500/30 rounded border border-teal-400/50"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="absolute -bottom-1 -right-1 lg:-bottom-2 lg:-right-2 w-10 lg:w-12 h-10 lg:h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <Icon icon="mdi:android" size={20} class="text-white" />
                  </div>
                </div>
                <h3 class="text-lg lg:text-xl font-bold mb-3 lg:mb-4 text-success">Android Development</h3>
                <p class="text-foreground-subtle mb-4 lg:mb-6 leading-relaxed text-sm lg:text-base">
                  <strong class="text-foreground">Kotlin y Jetpack Compose</strong> para velocidad, modularidad y calidad a escala. Arquitectura modular y optimización.
                </p>
                <div class="space-y-1.5 lg:space-y-2 w-full">
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-green-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Arquitectura modular</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-green-500 flex-shrink-0" />
                    <span class="text-foreground-muted">CI/CD para Play</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-green-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Optimización de ANRs</span>
                  </div>
                  <div class="flex items-center gap-2 lg:gap-3 text-xs lg:text-sm">
                    <Icon icon="material-symbols-light:check-circle-outline" size={14} class="text-green-500 flex-shrink-0" />
                    <span class="text-foreground-muted">Observabilidad</span>
                  </div>
                </div>
              </div>
            </div>
            {/* CTA de evaluación móvil */}
            <div class="text-center">
              <div class="inline-flex items-center gap-3 px-4 md:px-6 py-2 md:py-3 bg-surface-hover/80 rounded-full border border-border-strong mb-6 md:mb-8">
                <Icon icon="material-symbols-light:smartphone-outline" size={18} class="text-primary" />
                <span class="text-foreground-muted font-semibold text-sm lg:text-base">Evaluar viabilidad</span>
                <Button size="sm" color="primary" class="ml-2" onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}>
                  Análisis gratuito
                </Button>
              </div>
              <p class="text-foreground-subtle max-w-2xl mx-auto leading-relaxed text-sm lg:text-base">
                Análisis gratuito de tu proyecto móvil. Descubre qué tecnología es la mejor para tu caso de uso, presupuesto y timeline.
              </p>
            </div>
          </Container>
        </section>

        {/* TESTIMONIOS/CONFIANZA */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <div class="absolute inset-0 bg-slate-50/30 dark:bg-slate-900/20 -z-10"></div>
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="success" class="mb-6">
                La confianza de nuestros clientes
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">Resultados que hablan por sí solos</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">Más de 50 empresas han confiado en nosotros para transformar sus ideas en productos digitales exitosos.</p>
            </div>

            {/* Testimonios grid */}
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Testimonio 1 */}
              <Card class="p-6 hover:shadow-xl transition-all duration-300">
                <div class="flex items-center gap-1 mb-4">
                  <Icon icon="material-symbols-light:star" size={16} class="text-amber-400" />
                  <Icon icon="material-symbols-light:star" size={16} class="text-amber-400" />
                  <Icon icon="material-symbols-light:star" size={16} class="text-amber-400" />
                  <Icon icon="material-symbols-light:star" size={16} class="text-amber-400" />
                  <Icon icon="material-symbols-light:star" size={16} class="text-amber-400" />
                </div>
                <p class="text-foreground-subtle mb-4">"Increíble velocidad de desarrollo sin sacrificar calidad. En 3 días teníamos nuestra página funcionando perfectamente."</p>
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span class="text-white font-semibold text-sm">M.R</span>
                  </div>
                  <div>
                    <div class="font-semibold text-foreground">Cristian </div>
                    <div class="text-sm text-slate-500">CEO</div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Métricas de confianza */}
            <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div class="text-3xl font-bold text-foreground mb-2">100%</div>
                <div class="text-foreground-subtle">Proyectos entregados a tiempo</div>
              </div>
              <div>
                <div class="text-3xl font-bold text-foreground mb-2">2 años</div>
                <div class="text-foreground-subtle">Soporte promedio</div>
              </div>
              <div>
                <div class="text-3xl font-bold text-foreground mb-2">50+</div>
                <div class="text-foreground-subtle">Empresas satisfechas</div>
              </div>
              <div>
                <div class="text-3xl font-bold text-foreground mb-2">24h</div>
                <div class="text-foreground-subtle">Tiempo de respuesta</div>
              </div>
            </div>
          </Container>
        </section>

        {/* PROCESO SIMPLIFICADO */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="primary" class="mb-6">
                Proceso probado
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">De la idea al lanzamiento en 4 pasos</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">
                Un proceso ágil y transparente que minimiza riesgos y maximiza resultados. Sin sorpresas, con entregables claros.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Paso 1 */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500/15 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:explore-outline" size={32} class="text-info" />
                </div>
                <div class="mb-4">
                  <div class="inline-flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-bold mb-3">1</div>
                  <h3 class="text-xl font-bold text-foreground">Descubrimiento</h3>
                </div>
                <p class="text-foreground-subtle leading-relaxed mb-4">Entendemos tu visión, analizamos el mercado y definimos objetivos medibles.</p>
                <div class="text-sm font-medium text-info">1 semana • Workshop gratuito</div>
              </div>

              {/* Paso 2 */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:palette-outline" size={32} class="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div class="mb-4">
                  <div class="inline-flex items-center justify-center w-8 h-8 bg-emerald-600 text-white rounded-full text-sm font-bold mb-3">2</div>
                  <h3 class="text-xl font-bold text-foreground">Diseño & Prototipo</h3>
                </div>
                <p class="text-foreground-subtle leading-relaxed mb-4">Creamos wireframes, prototipos interactivos y validamos la experiencia de usuario.</p>
                <div class="text-sm font-medium text-emerald-600 dark:text-emerald-400">1-2 semanas • Prototipos clickeables</div>
              </div>

              {/* Paso 3 */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/15 to-purple-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:code" size={32} class="text-purple-600 dark:text-purple-400" />
                </div>
                <div class="mb-4">
                  <div class="inline-flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full text-sm font-bold mb-3">3</div>
                  <h3 class="text-xl font-bold text-foreground">Desarrollo</h3>
                </div>
                <p class="text-foreground-subtle leading-relaxed mb-4">Sprints de 1-2 semanas con demos constantes y feedback integrado.</p>
                <div class="text-sm font-medium text-purple-600 dark:text-purple-400">4-8 semanas • Demos semanales</div>
              </div>

              {/* Paso 4 */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-500/15 to-orange-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:rocket-launch-outline" size={32} class="text-orange-600 dark:text-orange-400" />
                </div>
                <div class="mb-4">
                  <div class="inline-flex items-center justify-center w-8 h-8 bg-orange-600 text-white rounded-full text-sm font-bold mb-3">4</div>
                  <h3 class="text-xl font-bold text-foreground">Lanzamiento</h3>
                </div>
                <p class="text-foreground-subtle leading-relaxed mb-4">Deploy, monitoreo 24/7 y soporte post-lanzamiento para un crecimiento sostenible.</p>
                <div class="text-sm font-medium text-orange-600 dark:text-orange-400">Día 1 • Soporte incluido</div>
              </div>
            </div>

            {/* CTA del proceso */}
            <div class="mt-16 flex flex-wrap items-center justify-center gap-6">
              <div class="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-700/30 rounded-full border border-border text-center">
                <Icon icon="material-symbols-light:schedule-outline" size={20} class="text-foreground-subtle" />
                <span class="text-foreground-muted font-semibold">Timeline típico: 6-12 semanas desde kick-off hasta lanzamiento</span>
              </div>
              <Button
                size="lg"
                color="primary"
                startIcon="material-symbols-light:calendar-month-outline"
                class="shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Programar workshop gratuito
              </Button>
            </div>
          </Container>
        </section>

        {/* STACK TECNOLÓGICO SIMPLIFICADO */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <div class="absolute inset-0 bg-slate-50/30 dark:bg-slate-900/20 -z-10"></div>
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="secondary" class="mb-6">
                Stack tecnológico
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">Tecnologías de vanguardia</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">
                Utilizamos las mejores herramientas del mercado para garantizar performance, escalabilidad y mantenibilidad.
              </p>
            </div>

            {/* Tecnologías principales */}
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-16">
              {/* React */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:react" size={32} class="text-info" />
                </div>
                <div class="font-semibold text-foreground">React</div>
                <div class="text-sm text-foreground-subtle">Frontend</div>
              </div>

              {/* Node.js */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500/15 to-green-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:nodejs" size={32} class="text-success" />
                </div>
                <div class="font-semibold text-foreground">Node.js</div>
                <div class="text-sm text-foreground-subtle">Backend</div>
              </div>

              {/* PostgreSQL */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-indigo-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="logos:postgresql" size={32} class="text-on-info-soft" />
                </div>
                <div class="font-semibold text-foreground">PostgreSQL</div>
                <div class="text-sm text-foreground-subtle">Database</div>
              </div>

              {/* AWS */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500/15 to-orange-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:aws" size={32} class="text-orange-600 dark:text-orange-400" />
                </div>
                <div class="font-semibold text-foreground">AWS</div>
                <div class="text-sm text-foreground-subtle">Cloud</div>
              </div>

              {/* TypeScript */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-slate-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:language-typescript" size={32} class="text-info" />
                </div>
                <div class="font-semibold text-foreground">TypeScript</div>
                <div class="text-sm text-foreground-subtle">Language</div>
              </div>

              {/* Docker */}
              <div class="text-center group">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-cyan-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:docker" size={32} class="text-info" />
                </div>
                <div class="font-semibold text-foreground">Docker</div>
                <div class="text-sm text-foreground-subtle">DevOps</div>
              </div>
            </div>

            {/* Beneficios del stack */}
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-emerald-500/15 to-emerald-600/10 flex items-center justify-center">
                  <Icon icon="material-symbols-light:trending-up" size={24} class="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">Alto rendimiento</h3>
                <p class="text-foreground-subtle">Optimización desde el primer día para cargas rápidas y experiencias fluidas.</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-blue-500/15 to-blue-600/10 flex items-center justify-center">
                  <Icon icon="material-symbols-light:bar-chart" size={24} class="text-info" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">Escalabilidad</h3>
                <p class="text-foreground-subtle">Arquitectura preparada para crecer desde 100 hasta millones de usuarios.</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-purple-500/15 to-purple-600/10 flex items-center justify-center">
                  <Icon icon="material-symbols-light:verified-outline" size={24} class="text-purple-600 dark:text-purple-400" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">Mantenibilidad</h3>
                <p class="text-foreground-subtle">Código limpio y documentado que facilita futuras actualizaciones y mejoras.</p>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ SIMPLIFICADO */}
        <section use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="warning" class="mb-6">
                Preguntas frecuentes
              </Chip>
              <h2 class="text-4xl md:text-5xl font-bold text-foreground mb-6">Todo lo que necesitas saber</h2>
              <p class="max-w-3xl mx-auto text-lg text-foreground-muted leading-relaxed">Respuestas claras a las preguntas más comunes sobre nuestro proceso y servicios.</p>
            </div>
            <div class="mt-6">
              <AccordionGroup>
                <Accordion title="¿Cómo cotizan un proyecto y qué información necesitan?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Entradas necesarias: objetivos, usuarios, success metrics, alcances deseados y acceso a documentación existente.</li>
                    <li>Entregables de la cotización: opciones de alcance (MVP vs fases), estimación personalizada según requerimientos, hitos y riesgos principales.</li>
                    <li>Modelos: precio fijo por fase o tiempo y materiales con reportes semanales.</li>
                  </ul>
                  <p class="mt-2">Siguiente paso recomendado: agendar una sesión de discovery para convertir incertidumbres en alcance y cifras.</p>
                </Accordion>

                <Accordion title="¿Cuánto tarda un MVP funcional?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Discovery: fase inicial para hipótesis y prioridades.</li>
                    <li>Diseño & prototipo: creación de interacción y tests rápidos.</li>
                    <li>Desarrollo núcleo: implementación de features esenciales, QA y despliegue.</li>
                  </ul>
                  <p class="mt-2">Factores que alteran la duración: complejidad de integraciones, cumplimiento regulatorio y disponibilidad de decisiones del cliente.</p>
                </Accordion>

                <Accordion title="¿Quién queda con el código y cómo lo entregan?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Flujo habitual: repositorio privado (GitHub/GitLab) con rama principal protegida, CI configurado y documentación de despliegue.</li>
                    <li>Opciones: transferencia total del repo, acceso organizacional o mantenimiento gestionado por nosotros.</li>
                    <li>Licencias y terceros: listamos dependencias y licencias en la entrega.</li>
                  </ul>
                </Accordion>

                <Accordion title="¿Qué incluye el soporte post‑lanzamiento?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Corrección de bugs críticos y ajustes menores durante el periodo incluido.</li>
                    <li>Planes: retainer mensual o ticket-based con SLAs según nivel.</li>
                    <li>Opcional: monitoring, alerting y runbook de incidentes como servicio adicional.</li>
                  </ul>
                </Accordion>

                <Accordion title="¿Cómo se integran con equipos internos y herramientas?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Comunicación: Slack/Teams + reuniones periódicas (dailies o syncs según necesidad).</li>
                    <li>Gestión: Jira/Trello para backlog y PRs en GitHub/GitLab con revisiones y CI/CD.</li>
                    <li>Handover: documentación de arquitectura, runbooks y capacitación rápida para el equipo interno.</li>
                  </ul>
                </Accordion>

                <Accordion title="¿Cómo gestionan cambios de alcance durante el proyecto?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Proceso: solicitud → impacto → aprobación → ajuste del plan.</li>
                    <li>Pequeños cambios pueden entrar en el siguiente sprint; cambios mayores requieren enmienda al contrato o fase adicional.</li>
                    <li>Recomendamos sprints cortos y priorización continua para reducir riesgos.</li>
                  </ul>
                </Accordion>

                <Accordion title="Seguridad y cumplimiento (GDPR, data handling)">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Prácticas: análisis OWASP, HTTPS forzado, cifrado en reposo cuando aplica y gestión de accesos.</li>
                    <li>Compliance: si se requiere, incluimos auditoría y documentación para GDPR/ISO en el alcance.</li>
                    <li>Opcional: pentest y revisión de infraestructura como servicio adicional.</li>
                  </ul>
                </Accordion>

                <Accordion title="¿Cuánto cuesta el mantenimiento a largo plazo?">
                  <ul class="mt-2 list-disc pl-5 text-sm text-foreground-subtle">
                    <li>Ofrecemos paquetes personalizados según requerimientos.</li>
                    <li>Ofrecemos paquetes escalables y reporting mensual con métricas de salud y actividad.</li>
                  </ul>
                </Accordion>

                <Accordion title="¿Cómo empezamos?">
                  <ol class="mt-2 list-decimal pl-5 text-sm text-foreground-subtle">
                    <li>Sesión de discovery para definir objetivos y prioridades.</li>
                    <li>Propuesta con alcance, entregables y estimación personalizada.</li>
                    <li>Firma y plan de trabajo; kickoff y primer sprint.</li>
                  </ol>
                </Accordion>
              </AccordionGroup>
            </div>
          </Container>
        </section>

        {/* CTA FINAL MEJORADO */}
        <section id="contacto" use:reveal={{ y: 40, target: '.grid > *', stagger: 0.12 }} class="relative py-20">
          <div class="absolute inset-0 bg-gradient-to-b from-primary-50/30 to-transparent dark:from-primary-900/10 -z-10"></div>
          <Container>
            <div class="text-center mb-16">
              <Chip variant="soft" color="primary" class="mb-6">
                ¿Listo para empezar?
              </Chip>
              <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Transformemos tu idea en
                <span class="block mt-2 bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-600 text-transparent bg-clip-text">realidad digital</span>
              </h2>
              <p class="text-lg md:text-xl text-foreground-muted max-w-4xl mx-auto leading-relaxed mb-8">
                Agenda una <strong>consulta gratuita de 30 minutos</strong> y descubre cómo podemos impulsar tu negocio con tecnología de vanguardia.
              </p>

              {/* CTA principal destacado */}
              <div class="mb-12">
                <Button
                  size="xl"
                  color="primary"
                  startIcon="material-symbols-light:calendar-month-outline"
                  class="group relative overflow-hidden px-12 py-6 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 mb-4"
                  onClick={() => window.open(whatsapp('Hola, me gustaría agendar una consulta gratuita para mi proyecto'), '_blank')}
                >
                  Agendar consulta gratuita
                </Button>
                <div class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-foreground-subtle">
                  <span class="inline-flex items-center gap-1.5">
                    <Icon icon="material-symbols-light:check-circle-outline" width={16} class="text-success" /> Sin compromiso
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <Icon icon="material-symbols-light:check-circle-outline" width={16} class="text-success" /> Respuesta en 24h
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <Icon icon="material-symbols-light:check-circle-outline" width={16} class="text-success" /> Análisis inicial incluido
                  </span>
                </div>
              </div>
            </div>

            {/* Alternativas de contacto */}
            <div class="text-center mb-8">
              <p class="text-foreground-subtle mb-6">O elige tu método de contacto preferido:</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {/* WhatsApp */}
              <Card class="p-6 text-center hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-green-200 dark:hover:border-green-800">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500/15 to-green-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="mdi:whatsapp" size={32} class="text-success" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">WhatsApp</h3>
                <p class="text-foreground-subtle mb-3 text-sm">Respuesta inmediata para consultas rápidas</p>
                <p class="text-sm font-medium text-foreground mb-4 tabular-nums">{SITE.contact.phone}</p>
                <Button color="success" size="sm" fullWidth startIcon="mdi:whatsapp" onClick={() => window.open(whatsapp('Hola, me gustaría conocer más sobre sus servicios de desarrollo'), '_blank')}>
                  Escribir ahora
                </Button>
              </Card>

              {/* Email */}
              <Card class="p-6 text-center hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-800">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:mail-outline" size={32} class="text-info" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">Email</h3>
                <p class="text-foreground-subtle mb-3 text-sm">Para propuestas detalladas y documentación</p>
                <a
                  href={SITE.contact.emailHref}
                  class="block text-sm font-medium text-primary hover:underline mb-4 break-all rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                >
                  {SITE.contact.email}
                </a>
                <Button
                  color="primary"
                  variant="outlined"
                  size="sm"
                  fullWidth
                  startIcon="material-symbols-light:mail-outline"
                  onClick={() => window.open(mailto('Consulta sobre desarrollo de software'), '_self')}
                >
                  Enviar email
                </Button>
              </Card>

              {/* Llamada */}
              <Card class="p-6 text-center hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-purple-200 dark:hover:border-purple-800">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/15 to-purple-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon icon="material-symbols-light:call-outline" size={32} class="text-purple-600 dark:text-purple-400" />
                </div>
                <h3 class="text-lg font-bold text-foreground mb-2">Llamada</h3>
                <p class="text-foreground-subtle mb-3 text-sm">Para proyectos complejos y consultoría</p>
                <p class="text-sm font-medium text-foreground mb-4 tabular-nums">{SITE.contact.phone}</p>
                <Button color="secondary" variant="outlined" size="sm" fullWidth startIcon="material-symbols-light:call-outline" onClick={() => window.open(SITE.contact.phoneHref, '_self')}>
                  Llamar ahora
                </Button>
              </Card>
            </div>

            {/* Garantía final */}
            <div class="text-center mt-16 p-8 bg-surface-muted rounded-2xl border border-border">
              <Icon icon="material-symbols-light:verified-outline" size={48} class="text-emerald-500 mx-auto mb-4" />
              <h3 class="text-xl font-bold text-foreground mb-2">Garantía de satisfacción 100%</h3>
              <p class="text-foreground-subtle max-w-2xl mx-auto">
                Si no estás completamente satisfecho con nuestro trabajo en los primeros 30 días, te devolvemos tu dinero sin preguntas. Tu éxito es nuestra prioridad.
              </p>
            </div>
          </Container>
        </section>
      </div>
    </>
  )
}

export default LandingLayout
